// bluaIQ — Gantt Demo Data
// Realistic 6-month DFW spec home schedule with dependencies

export interface GanttTask {
  id: string
  name: string
  nameEs: string
  phase: string
  phaseEs: string
  status?: string
  startDate: string
  endDate: string
  progress: number
  dependencies: string[]
  color: string
  assignee: string
  isCritical?: boolean
  source?: string
  authority?: string
  externalKey?: string
  blocking?: boolean
  requiredUploads?: string[]
  // Saved free-text note; surfaced as the Phase 1 blocker reason in the Gantt
  // edit panel. Returned by GET /tasks (LifecycleTask.notes) — may be null.
  notes?: string | null
}

export const DEMO_GANTT_TASKS: GanttTask[] = [
  {
    id: 'g-1', name: 'Site Work & Grading', nameEs: 'Preparación del Terreno',
    phase: 'Pre-Construction', phaseEs: 'Pre-Construcción',
    startDate: '2026-01-12', endDate: '2026-01-23', progress: 100,
    dependencies: [], color: '#06B6D4', assignee: 'Solid Ground LLC',
  },
  {
    id: 'g-2', name: 'Foundation', nameEs: 'Cimentación',
    phase: 'Foundation', phaseEs: 'Cimentación',
    startDate: '2026-01-26', endDate: '2026-02-13', progress: 100,
    dependencies: ['g-1'], color: '#2196F3', assignee: 'Solid Ground LLC', isCritical: true,
  },
  {
    id: 'g-3', name: 'Framing', nameEs: 'Estructura',
    phase: 'Framing', phaseEs: 'Estructura',
    startDate: '2026-02-16', endDate: '2026-03-20', progress: 85,
    dependencies: ['g-2'], color: '#14B8A6', assignee: 'Mendez Framing LLC', isCritical: true,
  },
  {
    id: 'g-4', name: 'Roofing', nameEs: 'Techo',
    phase: 'Roofing', phaseEs: 'Techo',
    startDate: '2026-03-23', endDate: '2026-04-03', progress: 0,
    dependencies: ['g-3'], color: '#EC4899', assignee: 'Beacon Roofing', isCritical: true,
  },
  {
    id: 'g-5', name: 'Plumbing Rough-In', nameEs: 'Plomería Rough-In',
    phase: 'MEP', phaseEs: 'MEP',
    startDate: '2026-03-23', endDate: '2026-04-10', progress: 0,
    dependencies: ['g-3'], color: '#EF4444', assignee: 'Torres Plumbing',
  },
  {
    id: 'g-6', name: 'Electrical Rough-In', nameEs: 'Eléctrico Rough-In',
    phase: 'MEP', phaseEs: 'MEP',
    startDate: '2026-03-23', endDate: '2026-04-10', progress: 0,
    dependencies: ['g-3'], color: '#EF4444', assignee: 'Spark Electric Co',
  },
  {
    id: 'g-7', name: 'HVAC Rough-In', nameEs: 'HVAC Rough-In',
    phase: 'MEP', phaseEs: 'MEP',
    startDate: '2026-03-30', endDate: '2026-04-10', progress: 0,
    dependencies: ['g-3'], color: '#EF4444', assignee: 'ComfortAir HVAC',
  },
  {
    id: 'g-8', name: 'Insulation', nameEs: 'Aislamiento',
    phase: 'Insulation', phaseEs: 'Aislamiento',
    startDate: '2026-04-13', endDate: '2026-04-17', progress: 0,
    dependencies: ['g-5', 'g-6', 'g-7'], color: '#F59E0B', assignee: 'DFW Insulation Co', isCritical: true,
  },
  {
    id: 'g-9', name: 'Drywall', nameEs: 'Tablaroca',
    phase: 'Drywall', phaseEs: 'Tablaroca',
    startDate: '2026-04-20', endDate: '2026-05-08', progress: 0,
    dependencies: ['g-8'], color: '#22C55E', assignee: 'ProBoard Drywall', isCritical: true,
  },
  {
    id: 'g-13', name: 'Exterior Siding & Masonry', nameEs: 'Fachada y Mampostería',
    phase: 'Exterior', phaseEs: 'Exterior',
    startDate: '2026-04-20', endDate: '2026-05-10', progress: 0,
    dependencies: ['g-4'], color: '#10B981', assignee: 'Lone Star Masonry',
  },
  {
    id: 'g-10', name: 'Interior Paint', nameEs: 'Pintura Interior',
    phase: 'Finishes', phaseEs: 'Acabados',
    startDate: '2026-05-11', endDate: '2026-05-22', progress: 0,
    dependencies: ['g-9'], color: '#6366F1', assignee: 'Torres Painting',
  },
  {
    id: 'g-11', name: 'Flooring & Tile', nameEs: 'Pisos y Azulejo',
    phase: 'Finishes', phaseEs: 'Acabados',
    startDate: '2026-05-11', endDate: '2026-05-29', progress: 0,
    dependencies: ['g-9'], color: '#6366F1', assignee: 'FloorCraft DFW', isCritical: true,
  },
  {
    id: 'g-12', name: 'Cabinets & Countertops', nameEs: 'Gabinetes y Cubiertas',
    phase: 'Finishes', phaseEs: 'Acabados',
    startDate: '2026-05-25', endDate: '2026-06-05', progress: 0,
    dependencies: ['g-10'], color: '#6366F1', assignee: 'Texas Stone & Cabinet',
  },
  {
    id: 'g-14', name: 'MEP Trim & Fixtures', nameEs: 'Acabados MEP',
    phase: 'Finishes', phaseEs: 'Acabados',
    startDate: '2026-06-01', endDate: '2026-06-12', progress: 0,
    dependencies: ['g-11', 'g-12'], color: '#6366F1', assignee: 'Multiple Subs', isCritical: true,
  },
  {
    id: 'g-15', name: 'Landscaping & Concrete', nameEs: 'Paisajismo y Concreto',
    phase: 'Exterior', phaseEs: 'Exterior',
    startDate: '2026-06-08', endDate: '2026-06-19', progress: 0,
    dependencies: ['g-13'], color: '#10B981', assignee: 'GreenScape DFW',
  },
  {
    id: 'g-16', name: 'Final Inspections & Punch List', nameEs: 'Inspecciones Finales y Detalles',
    phase: 'Closeout', phaseEs: 'Cierre',
    startDate: '2026-06-22', endDate: '2026-07-03', progress: 0,
    dependencies: ['g-14', 'g-15'], color: '#9E9890', assignee: 'Maria Lopez', isCritical: true,
  },
]
